// src/components/workloads/creation/AddressListBuilder.tsx
import React, { useMemo, useCallback } from 'react';
import {
  List,
  Grid3X3,
  Check,
  X,
  Trash2,
  SplitSquareVertical,
  Users,
  MapPin,
  Route,
  Download,
} from 'lucide-react';
import { useWorkloadStore, CreationAddress } from '../../../store/workloadStore';
import GoogleMapProvider from '../../map/GoogleMapProvider';
import BaseMap from '../../map/BaseMap';
import MarkerManager, { MarkerData } from '../../map/MarkerManager';
import { previewWorkloadSplit, getUniqueStreets } from '../../../utils/addressSplitting';
import { exportCreationAddressesToCSV, AddressSource } from '../../../utils/addressExport';

const AddressListBuilder: React.FC = () => {
  const {
    creation,
    toggleAddressSelection,
    selectAllCreationAddresses,
    deselectAllCreationAddresses,
    setListSplitting,
  } = useWorkloadStore();

  const selectedCount = creation.addresses.filter((a) => a.selected).length;
  const totalCount = creation.addresses.length;

  // Convert addresses to markers
  const markers: MarkerData[] = useMemo(() => {
    return creation.addresses.map((addr, index) => ({
      id: `addr_${index}`,
      position: addr.location,
      status: addr.selected ? 'pending' : 'inaccessible', // Use different colors
      title: addr.street,
    }));
  }, [creation.addresses]);

  // Calculate map center from addresses
  const mapCenter = useMemo(() => {
    if (creation.addresses.length === 0) {
      return { lat: 39.8283, lng: -98.5795 };
    }

    const sum = creation.addresses.reduce(
      (acc, addr) => ({
        lat: acc.lat + addr.location.lat,
        lng: acc.lng + addr.location.lng,
      }),
      { lat: 0, lng: 0 }
    );

    return {
      lat: sum.lat / creation.addresses.length,
      lng: sum.lng / creation.addresses.length,
    };
  }, [creation.addresses]);

  // Get unique streets for display
  const uniqueStreets = useMemo(() => {
    return getUniqueStreets(creation.addresses as CreationAddress[]);
  }, [creation.addresses]);

  // Calculate workload split preview using smart splitting
  const splitPreview = useMemo(() => {
    if (!creation.listSplitting.enabled || selectedCount === 0) {
      return {
        workloadCount: 1,
        workloads: [{ name: creation.name || 'Workload', addressCount: selectedCount, streets: uniqueStreets.slice(0, 3) }]
      };
    }

    const selectedAddresses = creation.addresses.filter(a => a.selected) as CreationAddress[];
    const { maxSize, splitByStreet, splitByPolygon } = creation.listSplitting;

    // Use smart splitting if enabled
    if (splitByStreet || splitByPolygon) {
      return previewWorkloadSplit(selectedAddresses, {
        maxSize,
        splitByStreet,
        splitByPolygon,
      });
    }

    // Simple splitting by size only
    const numWorkloads = Math.ceil(selectedCount / maxSize);
    return {
      workloadCount: numWorkloads,
      workloads: Array.from({ length: numWorkloads }, (_, i) => {
        const start = i * maxSize;
        const count = Math.min(maxSize, selectedCount - start);
        return {
          name: `${creation.listSplitting.namePrefix} ${i + 1}`,
          addressCount: count,
          streets: [],
        };
      })
    };
  }, [creation.listSplitting, creation.addresses, creation.name, selectedCount, uniqueStreets]);

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Build Your Address Lists
        </h2>
        <p className="text-gray-600">
          Select which addresses to include and configure how to split them into workloads.
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Left: Address List */}
        <div>
          {/* Selection controls */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <button
                onClick={selectAllCreationAddresses}
                className="text-sm text-indigo-600 hover:text-indigo-700"
              >
                Select All
              </button>
              <span className="text-gray-300">|</span>
              <button
                onClick={deselectAllCreationAddresses}
                className="text-sm text-indigo-600 hover:text-indigo-700"
              >
                Deselect All
              </button>
              <span className="text-gray-300">|</span>
              <button
                onClick={() => {
                  // Export only the selected addresses, tagged with the
                  // creation type so the round-trip CSV preserves provenance.
                  const selected = creation.addresses.filter((a) => a.selected);
                  exportCreationAddressesToCSV(
                    selected,
                    (creation.type as AddressSource) || 'unknown'
                  );
                }}
                disabled={selectedCount === 0}
                className="text-sm text-indigo-600 hover:text-indigo-700 disabled:text-gray-400 disabled:cursor-not-allowed flex items-center gap-1"
              >
                <Download className="w-3 h-3" />
                Export CSV
              </button>
            </div>
            <span className="text-sm text-gray-500">
              {selectedCount} of {totalCount} selected
            </span>
          </div>

          {/* Address list */}
          <div className="border border-gray-200 rounded-lg max-h-96 overflow-y-auto">
            {creation.addresses.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <List className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>No addresses loaded yet.</p>
                <p className="text-sm">Go back and select an area or upload a file.</p>
              </div>
            ) : (
              <ul className="divide-y divide-gray-200">
                {creation.addresses.map((addr, index) => (
                  <li
                    key={index}
                    className={`flex items-center p-3 hover:bg-gray-50 ${
                      addr.selected ? '' : 'bg-gray-50 opacity-60'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={addr.selected}
                      onChange={() => toggleAddressSelection(index)}
                      className="h-4 w-4 text-indigo-600 rounded border-gray-300"
                    />
                    <div className="ml-3 flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {addr.street}
                        {addr.unit && <span className="text-gray-500"> Unit {addr.unit}</span>}
                      </p>
                      <p className="text-xs text-gray-500">
                        {addr.city}, {addr.state} {addr.zipCode}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Right: Map and Splitting */}
        <div className="space-y-6">
          {/* Map */}
          <div className="rounded-lg overflow-hidden border border-gray-200">
            <GoogleMapProvider>
              <BaseMap center={mapCenter} zoom={13} height={250}>
                <MarkerManager markers={markers} />
              </BaseMap>
            </GoogleMapProvider>
          </div>

          {/* List Splitting Configuration */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <SplitSquareVertical className="h-5 w-5 text-gray-400 mr-2" />
                <h3 className="font-medium text-gray-900">Split Into Multiple Workloads</h3>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={creation.listSplitting.enabled}
                  onChange={(e) => setListSplitting({ enabled: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
              </label>
            </div>

            {creation.listSplitting.enabled && (
              <div className="space-y-4">
                {/* Smart Splitting Options */}
                <div className="bg-white rounded-lg p-3 border border-gray-200">
                  <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
                    <Route className="h-4 w-4 mr-2 text-indigo-500" />
                    Smart Splitting Options
                  </h4>

                  <div className="space-y-3">
                    <label className="flex items-start cursor-pointer">
                      <input
                        type="checkbox"
                        checked={creation.listSplitting.splitByStreet}
                        onChange={(e) => setListSplitting({ splitByStreet: e.target.checked })}
                        className="h-4 w-4 text-indigo-600 rounded border-gray-300 mt-0.5"
                      />
                      <div className="ml-2">
                        <span className="text-sm font-medium text-gray-700">
                          Group by street
                        </span>
                        <p className="text-xs text-gray-500">
                          Keep addresses on the same street together for efficient walking routes
                        </p>
                      </div>
                    </label>

                    {creation.polygons?.length > 1 && (
                      <label className="flex items-start cursor-pointer">
                        <input
                          type="checkbox"
                          checked={creation.listSplitting.splitByPolygon}
                          onChange={(e) => setListSplitting({ splitByPolygon: e.target.checked })}
                          className="h-4 w-4 text-indigo-600 rounded border-gray-300 mt-0.5"
                        />
                        <div className="ml-2">
                          <span className="text-sm font-medium text-gray-700">
                            Keep drawn areas separate
                          </span>
                          <p className="text-xs text-gray-500">
                            Each polygon you drew becomes a separate workload
                          </p>
                        </div>
                      </label>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Maximum addresses per workload
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="500"
                    value={creation.listSplitting.maxSize}
                    onChange={(e) =>
                      setListSplitting({ maxSize: Math.max(1, Math.min(500, Number(e.target.value))) })
                    }
                    className="w-full rounded-md border border-gray-300 px-3 py-2"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Recommended: 50-100 addresses for a single canvasser's daily workload
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Workload name prefix
                  </label>
                  <input
                    type="text"
                    value={creation.listSplitting.namePrefix}
                    onChange={(e) => setListSplitting({ namePrefix: e.target.value })}
                    placeholder="List"
                    className="w-full rounded-md border border-gray-300 px-3 py-2"
                  />
                </div>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={creation.listSplitting.autoName}
                    onChange={(e) => setListSplitting({ autoName: e.target.checked })}
                    className="h-4 w-4 text-indigo-600 rounded border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    Include main workload name in split names
                  </span>
                </label>

                {/* Street summary */}
                {uniqueStreets.length > 0 && (
                  <div className="pt-3 border-t border-gray-200">
                    <p className="text-xs text-gray-500">
                      <strong>{uniqueStreets.length}</strong> unique street{uniqueStreets.length !== 1 ? 's' : ''} found:
                      {' '}
                      {uniqueStreets.slice(0, 5).join(', ')}
                      {uniqueStreets.length > 5 && ` and ${uniqueStreets.length - 5} more...`}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Split Preview */}
          {selectedCount > 0 && (
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                <Users className="h-4 w-4 mr-2 text-gray-400" />
                Workload Preview ({splitPreview.workloadCount} workload{splitPreview.workloadCount !== 1 ? 's' : ''})
              </h4>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {splitPreview.workloads.slice(0, 10).map((workload, index) => (
                  <div
                    key={index}
                    className="p-2 bg-gray-50 rounded"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">
                        {workload.name}
                      </span>
                      <span className="text-sm text-gray-500">
                        {workload.addressCount} addresses
                      </span>
                    </div>
                    {workload.streets.length > 0 && (
                      <p className="text-xs text-gray-400 mt-1 truncate">
                        {workload.streets.slice(0, 3).join(', ')}
                        {workload.streets.length > 3 && '...'}
                      </p>
                    )}
                  </div>
                ))}
                {splitPreview.workloads.length > 10 && (
                  <p className="text-xs text-gray-400 text-center py-1">
                    + {splitPreview.workloads.length - 10} more workloads
                  </p>
                )}
              </div>
              <div className="mt-3 pt-3 border-t border-gray-100">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-500">Total workloads</span>
                  <span className="font-medium text-gray-700">{splitPreview.workloadCount}</span>
                </div>
                <div className="flex items-center justify-between text-xs mt-1">
                  <span className="text-gray-500">Addresses per workload (avg)</span>
                  <span className="font-medium text-gray-700">
                    {splitPreview.workloadCount > 0
                      ? Math.round(selectedCount / splitPreview.workloadCount)
                      : 0}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddressListBuilder;
